/**
 * Authentication Service for PBS
 * Handles login, logout, session management
 * Compatible with TWAS authentication system for single login mechanism
 */

import apiClient from './apiClient';

export interface User {
  id: number;
  login: string;
  username: string;
  name: string;
  firstname: string;
  lastname: string;
  mail: string;
  admin: boolean;
  role: string;
  available_roles?: string[];  // List of roles user can switch to
  status: number;
  created_on?: string;
  projects?: any[];
  roles?: any[];
  pbsRoles?: string[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  data: {
    user: User;
    roles: any[];
    projects: any[];
    session_id: string;
  };
  message: string;
}

class AuthService {
  private readonly STORAGE_KEYS = {
    SESSION_ID: 'sessionId',
    USER_DATA: 'userData',
  };

  /**
   * Get the appropriate redirect path based on user roles
   * Priority: admin > project_owner
   */
  getRedirectPath(user?: User | null): string {
    if (!user) {
      return '/dashboard';
    }

    const pbsRoles = user.pbsRoles || [];
    const currentRole = user.role || '';

    // If user has admin role (in pbsRoles), redirect to admin page
    // Admin takes priority over project_owner
    if (pbsRoles.includes('admin')) {
      return '/admin/salaries';
    }

    // If user has project_owner role, redirect to dashboard
    if (pbsRoles.includes('project_owner') || currentRole === 'project_owner') {
      return '/dashboard';
    }

    // Default fallback
    return '/dashboard';
  }

  /**
   * Login user with email and password
   */
  async login(email: string, password: string = ''): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', {
        email,
        password,
      });

      const { session_id, user } = response.data.data;
      const normalizedUser = this.normalizeUser(user) ?? user;

      // Store session ID and user data in sessionStorage
      sessionStorage.setItem(this.STORAGE_KEYS.SESSION_ID, session_id);
      sessionStorage.setItem(this.STORAGE_KEYS.USER_DATA, JSON.stringify(normalizedUser));

      // Initialize permissions
      try {
        const { default: permissionService } = await import('./permissionService');
        await permissionService.initializePermissions();
      } catch (error) {
        console.error('Failed to initialize permissions:', error);
      }

      // Notify listeners about auth state change
      this.notifyAuthStateChanged(true, normalizedUser);

      return {
        ...response.data,
        data: {
          ...response.data.data,
          user: normalizedUser,
        },
      };
    } catch (error: any) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  /**
   * Login user with Google OAuth
   */
  async googleLogin(email: string, givenName?: string, familyName?: string): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/auth/google-login', {
        email,
        given_name: givenName,
        family_name: familyName,
      });

      const { session_id, user } = response.data.data;
      const normalizedUser = this.normalizeUser(user) ?? user;

      // Store session ID and user data in sessionStorage
      sessionStorage.setItem(this.STORAGE_KEYS.SESSION_ID, session_id);
      sessionStorage.setItem(this.STORAGE_KEYS.USER_DATA, JSON.stringify(normalizedUser));

      // Initialize permissions
      try {
        const { default: permissionService } = await import('./permissionService');
        await permissionService.initializePermissions();
      } catch (error) {
        console.error('Failed to initialize permissions:', error);
      }

      // Notify listeners about auth state change
      this.notifyAuthStateChanged(true, normalizedUser);

      return {
        ...response.data,
        data: {
          ...response.data.data,
          user: normalizedUser,
        },
      };
    } catch (error: any) {
      console.error('Google login failed:', error);
      throw error;
    }
  }

  /**
   * Login using an existing SSO session token (shared across apps)
   */
  async loginWithSsoToken(token: string): Promise<boolean> {
    if (!token) {
      return false;
    }

    try {
      sessionStorage.setItem(this.STORAGE_KEYS.SESSION_ID, token);
      const user = await this.getCurrentUser();

      if (user) {
        try {
          const { default: permissionService } = await import('./permissionService');
          await permissionService.initializePermissions();
        } catch (error) {
          console.error('Failed to initialize permissions:', error);
        }

        this.notifyAuthStateChanged(true, user);
        return true;
      }
    } catch (error) {
      console.error('SSO login failed:', error);
    }

    this.clearSession();
    return false;
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      // Clear session data regardless of API call result
      this.clearSession();
      
      // Clear permissions
      try {
        const { default: permissionService } = await import('./permissionService');
        permissionService.clearPermissions();
      } catch (error) {
        console.error('Failed to clear permissions:', error);
      }
      
      this.notifyAuthStateChanged(false, null);
    }
  }

  /**
   * Get current user info from API
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiClient.get<{ success: boolean; data: User }>('/auth/me');
      
      if (response.data.success) {
        const normalizedUser = this.normalizeUser(response.data.data) ?? response.data.data;
        // Update stored user data
        sessionStorage.setItem(this.STORAGE_KEYS.USER_DATA, JSON.stringify(normalizedUser));
        return normalizedUser;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async checkAuth(): Promise<boolean> {
    try {
      const response = await apiClient.get<{ success: boolean; authenticated: boolean }>('/auth/check');
      return response.data.authenticated;
    } catch (error) {
      return false;
    }
  }

  /**
   * Switch user's active role
   */
  async switchRole(newRole: string): Promise<void> {
    try {
      const response = await apiClient.post('/auth/switch-role', {
        role: newRole,
      });

      if (response.data.success) {
        // Update stored user data with new role
        const userData = this.getUserData();
        if (userData) {
          userData.role = newRole;
          sessionStorage.setItem(this.STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
          
          // Notify listeners about role change
          this.notifyAuthStateChanged(true, userData);
        }
      }
    } catch (error: any) {
      console.error('Role switch failed:', error);
      throw error;
    }
  }

  /**
   * Get session ID from storage
   */
  getSessionId(): string | null {
    return sessionStorage.getItem(this.STORAGE_KEYS.SESSION_ID);
  }

  /**
   * Get user data from storage
   */
  getUserData(): User | null {
    const userData = sessionStorage.getItem(this.STORAGE_KEYS.USER_DATA);
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        console.error('Failed to parse user data:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Check if user is logged in (has session)
   */
  isAuthenticated(): boolean {
    const sessionId = this.getSessionId();
    const userData = this.getUserData();
    return !!(sessionId && userData);
  }

  /**
   * Clear session data from storage
   */
  clearSession(): void {
    sessionStorage.removeItem(this.STORAGE_KEYS.SESSION_ID);
    sessionStorage.removeItem(this.STORAGE_KEYS.USER_DATA);
  }

  /**
   * Auth state change listeners
   */
  private authStateListeners: Array<(isAuthenticated: boolean, user: User | null) => void> = [];

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback: (isAuthenticated: boolean, user: User | null) => void): () => void {
    this.authStateListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.authStateListeners = this.authStateListeners.filter(listener => listener !== callback);
    };
  }

  /**
   * Notify all listeners about auth state change
   */
  private notifyAuthStateChanged(isAuthenticated: boolean, user: User | null): void {
    this.authStateListeners.forEach(listener => {
      try {
        listener(isAuthenticated, user);
      } catch (error) {
        console.error('Error in auth state listener:', error);
      }
    });
  }

  hasPbsRole(role: string): boolean {
    if (!role) {
      return false;
    }
    const user = this.getUserData();
    return !!user?.pbsRoles?.includes(role);
  }

  getPbsRoles(): string[] {
    const user = this.getUserData();
    return user?.pbsRoles ? [...user.pbsRoles] : [];
  }

  private normalizeUser(user: User | null): User | null {
    if (!user) {
      return user;
    }
    const roles = (user.pbsRoles || (user as any).pbs_roles || []) as string[];
    const normalized: User = {
      ...user,
      pbsRoles: roles,
    };
    if (roles.includes('admin')) {
      normalized.role = 'admin';
    } else if (roles.includes('project_owner')) {
      normalized.role = 'project_owner';
    }
    return normalized;
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;


